import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
});

function PrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy — Weborbis Sport";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content", "How Weborbis Sport collects, uses and protects information at weborbis.top.");
    }
  }, []);
  return (
    <LegalPage title="Privacy Policy" updated="July 21, 2026">
      <p>
        This Privacy Policy applies to Weborbis Sport ("the Service") provided by Weborbis Sport
        ("we", "us", "our"). It explains how we collect, use, and share information about you when
        you use the Service available at <strong>weborbis.top</strong>. By using the Service, you
        agree to the collection and use of information as described in this policy.
      </p>

      <h2>1. Information We Collect</h2>
      <p>
        <strong>Personal Information:</strong> We may collect information that can identify you,
        such as your name, email address, when you voluntarily provide it (e.g., when you contact
        us).
      </p>
      <p>
        <strong>Usage Information:</strong> We automatically collect information about your
        interaction with the Service, such as IP address, browser type, pages visited, time spent,
        and device information.
      </p>
      <p>
        <strong>Cookies and Tracking Technologies:</strong> We use cookies and similar
        technologies to help the Service function and understand usage. You can control cookies
        through your browser settings.
      </p>

      <h2>2. Data Processing Details</h2>
      <p>
        We process your personal data based on the following legal grounds under the General Data
        Protection Regulation (GDPR) and other applicable laws:
      </p>
      <ul>
        <li>
          <strong>Consent:</strong> Where you have provided explicit consent for a specific
          purpose.
        </li>
        <li>
          <strong>Legitimate Interests:</strong> To operate and improve the Service, including
          analytics, security, and user experience optimization.
        </li>
        <li>
          <strong>Legal Obligations:</strong> To comply with applicable laws, regulations, or
          legal processes.
        </li>
      </ul>
      <p>
        <strong>Data Processing Activities:</strong>
      </p>
      <ul>
        <li>
          Server logs are retained for 30 days for security and operational purposes, then
          anonymized.
        </li>
        <li>
          Analytics data is aggregated and used to understand visitor behavior and improve content
          relevance.
        </li>
        <li>
          Communication data (emails) is retained for as long as necessary to respond to your
          inquiry.
        </li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service.</li>
        <li>Communicate with you, including responding to your inquiries.</li>
        <li>Personalize your experience and deliver relevant content.</li>
        <li>Ensure security and prevent fraud or abuse.</li>
        <li>Comply with legal obligations.</li>
      </ul>

      <h2>4. How We Share Your Information</h2>
      <p>We may share your information:</p>
      <ul>
        <li>
          With service providers who help us operate the Service (e.g., hosting, analytics, CDN).
        </li>
        <li>
          If required by law, regulation, legal process, or governmental request to protect our
          rights, property, or safety.
        </li>
        <li>
          In connection with a business transfer (e.g., merger, acquisition, or sale of assets).
        </li>
        <li>With your consent.</li>
      </ul>
      <p>We do not sell your personal information to third parties for commercial purposes.</p>

      <h2>5. Data Security</h2>
      <p>
        We take reasonable measures to protect your information from unauthorized access, use, or
        disclosure. These include implementing secure server infrastructure, encryption, access
        controls, and regular security audits. However, no internet transmission is completely
        secure, and we cannot guarantee absolute security.
      </p>

      <h2>6. Your Choices and Rights</h2>
      <p>
        You may have certain rights regarding your personal information, depending on your
        location. These rights include:
      </p>
      <ul>
        <li>The right to access your personal information.</li>
        <li>The right to correct inaccurate personal information.</li>
        <li>The right to delete your personal information.</li>
        <li>The right to object to processing based on legitimate interests.</li>
        <li>The right to data portability.</li>
        <li>The right to withdraw consent at any time.</li>
      </ul>
      <p>
        To exercise these rights or make a request, please contact us at{" "}
        <strong>privacy@weborbis.top</strong>.
      </p>

      <h2>7. Personalized Advertising</h2>
      <p>
        The Service may display personalized advertisements from third-party ad networks. You can
        opt out of personalized advertising by visiting the following links:
      </p>
      <ul>
        <li>
          Google:{" "}
          <a
            href="https://myadcenter.google.com/personalizationoff"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://myadcenter.google.com/personalizationoff
          </a>
        </li>
        <li>
          Google Partner Sites:{" "}
          <a
            href="https://policies.google.com/technologies/partner-sites"
            target="_blank"
            rel="noopener noreferrer"
          >
            https://policies.google.com/technologies/partner-sites
          </a>
        </li>
      </ul>
      <p>
        You can also manage cookie preferences through your browser settings to control tracking
        for advertising purposes.
      </p>

      <h2>8. Applicable Laws and Regulations</h2>
      <p>
        This Privacy Policy is designed to comply with the following data protection regulations:
      </p>
      <ul>
        <li>
          <strong>General Data Protection Regulation (GDPR)</strong> (EU Regulation 2016/679):
          Applicable to users within the European Union and European Economic Area.
        </li>
        <li>
          <strong>Lei Geral de Proteção de Dados (LGPD)</strong> (Brazilian Law No. 13.709/2018):
          Applicable to users within Brazil.
        </li>
        <li>
          <strong>California Consumer Privacy Act (CCPA/CPRA)</strong>: Applicable to users
          residing in California, United States.
        </li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is not intended for children under 13 years of age. We do not knowingly
        collect personal information from children under this age without parental consent. If you
        believe we have collected information from a child under 13, please contact us at{" "}
        <strong>privacy@weborbis.top</strong> to request deletion.
      </p>

      <h2>10. Third-Party Links</h2>
      <p>
        The Service may link to third-party websites or services. We have no control over, and
        assume no responsibility for, the content, privacy policies, or practices of any
        third-party websites or services. You acknowledge and agree that Weborbis Sport shall not
        be responsible or liable for any damage or loss caused by third-party sites.
      </p>

      <h2>11. Data Retention</h2>
      <p>
        We retain your personal information for as long as necessary to fulfill the purposes for
        which it was collected, including:
      </p>
      <ul>
        <li>Server logs: 30 days</li>
        <li>Analytics data: Aggregated and retained indefinitely</li>
        <li>Communication records: Until the inquiry is resolved, then 90 days</li>
      </ul>
      <p>
        After retention periods expire, data is either deleted or anonymized to prevent
        identification.
      </p>

      <h2>12. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will post changes on this page and
        update the "Last Updated" date. Your continued use of the Service after changes become
        effective means you accept the new policy. We will endeavor to provide reasonable notice of
        material changes.
      </p>

      <h2>13. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy, contact us at{" "}
        <strong>privacy@weborbis.top</strong>.
      </p>
    </LegalPage>
  );
}
